/**
 * Section form markers: an optional repeat count ("×2") and an optional
 * free-text performance note on a V3 section.
 *
 * Both are optional on `SectionStateV3` (`repeat?: number | null`,
 * `note?: string | null`); absent and null both mean "not set". Every
 * consumer (apps, PDF, Johan, Buffet) reads them through
 * `getSectionRepeat` / `getSectionNote` so the validity rules live here.
 */
/** Smallest stored repeat: a section played twice. Once is "not set". */
export const SECTION_REPEAT_MIN = 2;
/** Largest repeat the pickers offer and the normalizer keeps. */
export const SECTION_REPEAT_MAX = 8;
/** Maximum length of a section note, in characters. */
export const SECTION_NOTE_MAX_LENGTH = 200;
/**
 * Normalize a raw repeat value: non-numbers and values below 2 become
 * null (played once), fractions round, values above the max clamp to it.
 * Never throws, so a hand-edited file can't fail to import over it.
 */
export function normalizeSectionRepeat(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }
    const rounded = Math.round(value);
    if (rounded < SECTION_REPEAT_MIN) {
        return null;
    }
    return Math.min(rounded, SECTION_REPEAT_MAX);
}
/**
 * Normalize a raw note: unify line endings, drop control characters other
 * than newlines, trim, cap the length. Empty or non-string becomes null.
 * Pass `maxLength: Infinity` to keep the full text (Johan validates the
 * length instead, so the model is asked to shorten rather than being cut).
 */
export function normalizeSectionNote(value, maxLength = SECTION_NOTE_MAX_LENGTH) {
    if (typeof value !== 'string') {
        return null;
    }
    const cleaned = value
        .replace(/\r\n?/g, '\n')
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0009\u000B-\u001F\u007F]/g, '')
        .trim()
        .slice(0, maxLength)
        .trim();
    return cleaned.length > 0 ? cleaned : null;
}
/** The section's repeat count when one is set (2..8), else null. */
export function getSectionRepeat(section) {
    return normalizeSectionRepeat(section.repeat);
}
/** The section's note when one is set, else null. */
export function getSectionNote(section) {
    return normalizeSectionNote(section.note);
}
/** Display text for a repeat count: "×2". Never translated. */
export function formatSectionRepeat(repeat) {
    return `×${repeat}`;
}
